import { useMemo, useState } from "react";
import { ArrowRight, Download, FileSpreadsheet } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { useToast } from "../../context/ToastContext.js";
import { useLookup } from "../../context/LookupContext.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Badge } from "../../components/ui/badge.js";
import { Button } from "../../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { FileInput } from "../../components/ui/file-input.js";
import { SpecularButton } from "../../components/ui/specular-button.js";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table.js";
import { LockedNotice } from "../performance/shared.js";

type RowStatus = "ready" | "unchanged" | "error";

interface Change { from: string | null; to: string }

interface PreviewRow {
  rowNumber: number;
  employeeCode: string;
  sheetName: string;
  employee: { id: string; name: string; status: string } | null;
  status: RowStatus;
  errors: string[];
  warnings: string[];
  changes: { role?: Change; designation?: Change; reportingManager?: Change; department?: Change };
  apply: { employeeCode: string; role?: string; designation?: string; reportingManagerId?: string; departmentId?: string };
}

interface AnalyzeResult {
  columns: Record<string, string>;
  rows: PreviewRow[];
  counts: { total: number; ready: number; unchanged: number; errors: number; roleChanges: number };
}

interface CommitResult {
  updated: number;
  unchanged: number;
  failed: { employeeCode: string; reason: string }[];
}

const STATUS_BADGE: Record<RowStatus, { tone: "success" | "neutral" | "error"; label: string }> = {
  ready: { tone: "success", label: "Will update" },
  unchanged: { tone: "neutral", label: "No change" },
  error: { tone: "error", label: "Skipped" },
};

// The formatted Excel template, fetched with the session so the 401-refresh interceptor applies.
async function downloadTemplate() {
  const res = await api.get("/admin/employees/bulk-update/template", { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "employee-bulk-update-template.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ChangeLine({ label, change, roleName }: { label: string; change: Change | undefined; roleName?: (r: string) => string }) {
  if (!change) return null;
  const show = (v: string | null) => (v == null ? "—" : roleName ? roleName(v) : v);
  return (
    <p className="flex flex-wrap items-center gap-1">
      <span className="text-surface-500">{label}:</span>
      <span className="text-surface-500">{show(change.from)}</span>
      <ArrowRight size={12} className="shrink-0 text-surface-400" />
      <span className="font-medium">{show(change.to)}</span>
    </p>
  );
}

// Admin → Bulk Upload. Upload an Emp ID | Emp Name | Role | Designation | Immediate Mgr sheet
// (Department optional); each row is matched to an existing employee by Emp ID and the preview
// shows exactly what would change. Nothing is saved until "Apply".
export default function EmployeeBulkUploadPanel() {
  const toast = useToast();
  const roleLabels = useLookup("ROLE_LABEL");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AnalyzeResult | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<CommitResult | null>(null);

  const roleName = (r: string) => roleLabels.find((l) => l.value === r)?.displayName ?? r.replace(/_/g, " ");

  function reset() {
    setFile(null);
    setResult(null);
    setSelected(new Set());
    setDone(null);
  }

  async function analyze() {
    if (!file) throw new Error("validation");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<AnalyzeResult>("/admin/employees/bulk-update/analyze", form);
      setResult(data);
      setDone(null);
      setSelected(new Set(data.rows.filter((r) => r.status === "ready").map((r) => r.employeeCode)));
    } catch (e) {
      logError("EmployeeBulkUploadPanel.analyze", e);
      toast.show(errMsg(e) ?? "Could not read the file", "error");
      throw e;
    }
  }

  const toApply = useMemo(
    () => (result?.rows ?? []).filter((r) => r.status === "ready" && selected.has(r.employeeCode)),
    [result, selected],
  );
  const roleChanges = toApply.filter((r) => r.changes.role).length;

  async function commit() {
    try {
      const { data } = await api.post<CommitResult>("/admin/employees/bulk-update/commit", {
        rows: toApply.map((r) => r.apply),
      });
      setDone(data);
      toast.show(
        `${data.updated} employee${data.updated === 1 ? "" : "s"} updated${data.failed.length ? `, ${data.failed.length} failed` : ""}`,
        data.failed.length ? "error" : "success",
      );
    } catch (e) {
      logError("EmployeeBulkUploadPanel.commit", e);
      toast.show(errMsg(e) ?? "Could not apply the changes", "error");
      throw e;
    }
  }

  const toggle = (code: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(code);
      else next.delete(code);
      return next;
    });

  const readyRows = (result?.rows ?? []).filter((r) => r.status === "ready");
  const allSelected = readyRows.length > 0 && readyRows.every((r) => selected.has(r.employeeCode));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Employee bulk upload</CardTitle>
        <CardDescription>
          Update Role, Designation, Reporting Manager and (optionally) Department for many employees from one Excel sheet.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 text-[13px]">
        {!result ? (
          <>
            <div className="rounded-md bg-surface-50 px-4 py-3">
              <p className="font-medium">Columns: Emp ID · Emp Name · Role · Designation · Immediate Mgr · Department (optional)</p>
              <ul className="mt-1 list-disc pl-5 text-surface-600">
                <li>Each row is matched to an existing employee by <b>Emp ID</b> (e.g. INT-IN-996). Emp Name is only a cross-check.</li>
                <li>Immediate Mgr sets the <b>Reporting Manager</b>. Use the manager&apos;s name or Emp ID; ambiguous names are skipped, not guessed.</li>
                <li>Role takes the display name (Engineer, Manager, Team Lead…). Changing a role signs that employee out.</li>
                <li>Blank cells are left unchanged. You review every change before anything is saved.</li>
              </ul>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                title="Download a sample sheet in the upload format"
                onClick={() =>
                  void downloadTemplate().catch((e) => {
                    logError("EmployeeBulkUploadPanel.template", e);
                    toast.show(errMsg(e) ?? "Could not download the template", "error");
                  })
                }
              >
                <Download size={14} /> Download template
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <FileInput accept=".xlsx,.csv" value={file} onChange={setFile} placeholder="Choose a .xlsx or .csv file" />
              <AsyncButton disabled={!file} onClick={() => analyze()}>
                <FileSpreadsheet size={14} /> Scan sheet
              </AsyncButton>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="success">{result.counts.ready} to update</Badge>
              <Badge tone="neutral">{result.counts.unchanged} no change</Badge>
              {result.counts.errors > 0 && <Badge tone="error">{result.counts.errors} skipped</Badge>}
              {result.counts.roleChanges > 0 && <Badge tone="warning">{result.counts.roleChanges} role changes</Badge>}
              <span className="text-surface-500">{result.counts.total} rows in sheet</span>
            </div>

            {done && (
              <div className="rounded-md border border-surface-200 bg-surface-50 px-4 py-3">
                <p className="font-medium">
                  Done — {done.updated} updated, {done.unchanged} already up to date, {done.failed.length} failed.
                </p>
                {done.failed.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-[var(--color-error-fg)]">
                    {done.failed.map((f) => (
                      <li key={f.employeeCode}>{f.employeeCode}: {f.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {result.rows.length === 0 ? (
              <LockedNotice>No rows were found in this file.</LockedNotice>
            ) : (
              <div className="max-h-[55vh] overflow-auto rounded-md border border-surface-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 pl-3">
                        <Checkbox
                          checked={allSelected}
                          disabled={readyRows.length === 0 || !!done}
                          aria-label="Select all rows that will update"
                          onCheckedChange={(v) =>
                            setSelected(v === true ? new Set(readyRows.map((r) => r.employeeCode)) : new Set())
                          }
                        />
                      </TableHead>
                      <TableHead>Row</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Changes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.rows.map((r) => (
                      <TableRow key={`${r.rowNumber}-${r.employeeCode}`}>
                        <TableCell className="pl-3 align-top">
                          <Checkbox
                            checked={selected.has(r.employeeCode)}
                            disabled={r.status !== "ready" || !!done}
                            aria-label={`Apply row ${r.rowNumber}`}
                            onCheckedChange={(v) => toggle(r.employeeCode, v === true)}
                          />
                        </TableCell>
                        <TableCell className="align-top text-surface-500">{r.rowNumber}</TableCell>
                        <TableCell className="min-w-48 align-top">
                          <p className="font-medium">{r.employee?.name ?? (r.sheetName || "—")}</p>
                          <p className="text-[12px] text-surface-500">{r.employeeCode || "no Emp ID"}</p>
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge tone={STATUS_BADGE[r.status].tone}>{STATUS_BADGE[r.status].label}</Badge>
                        </TableCell>
                        <TableCell className="min-w-72 align-top">
                          <ChangeLine label="Role" change={r.changes.role} roleName={roleName} />
                          <ChangeLine label="Designation" change={r.changes.designation} />
                          <ChangeLine label="Reports to" change={r.changes.reportingManager} />
                          <ChangeLine label="Department" change={r.changes.department} />
                          {r.errors.map((m) => (
                            <p key={m} className="text-[var(--color-error-fg)]">{m}</p>
                          ))}
                          {r.warnings.map((m) => (
                            <p key={m} className="text-[var(--color-warning-fg)]">{m}</p>
                          ))}
                          {r.status === "unchanged" && r.warnings.length === 0 && (
                            <p className="text-surface-500">Already matches the sheet</p>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <SpecularButton size="sm" onClick={reset}>
                ← Upload another file
              </SpecularButton>
              {!done && (
                <AsyncButton
                  disabled={toApply.length === 0}
                  onClick={() =>
                    roleChanges > 0 &&
                    !window.confirm(`${roleChanges} role change${roleChanges === 1 ? "" : "s"} will sign those employees out and change what they can access. Continue?`)
                      ? undefined
                      : commit()
                  }
                >
                  Apply {toApply.length} change{toApply.length === 1 ? "" : "s"}
                </AsyncButton>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
